import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { ApiService } from 'src/app/services/api.service';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel, MatHint } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

export interface SyncMetaLeadsResult {
  hours: number;
  since: string;
  seen: number;
  created: number;
  skipped: number;
  failed: number;
  failures: { leadgen_id: string; reason: string }[];
  last_synced_at?: string | null;
}

@Component({
  selector: 'app-sync-meta-leads-dialog',
  templateUrl: './sync-meta-leads-dialog.component.html',
  styleUrls: ['./sync-meta-leads-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatHint,
    MatInput,
    MatDialogActions,
    MatButton,
    MatIcon,
  ],
})
export class SyncMetaLeadsDialogComponent implements OnInit {
  private fb = inject(FormBuilder);
  private api = inject(ApiService);
  dialogRef = inject(MatDialogRef<SyncMetaLeadsDialogComponent>);

  form: FormGroup = this.fb.group({
    hours: [24, [Validators.required, Validators.min(1), Validators.max(720)]],
  });

  syncing = false;
  error_message: string | null = null;
  result: SyncMetaLeadsResult | null = null;
  last_synced_at: string | null = null;

  ngOnInit(): void {
    this.api.getMetaLeadsLastSync().subscribe({
      next: data => {
        this.last_synced_at = data.last_synced_at ?? null;
      },
      error: () => {
        this.last_synced_at = null;
      },
    });
  }

  lastSyncedLabel(): string {
    if (!this.last_synced_at) {
      return 'Última actualización Meta: nunca';
    }

    const d = new Date(this.last_synced_at);
    if (Number.isNaN(d.getTime())) {
      return 'Última actualización Meta: —';
    }

    const date = d.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'Europe/Madrid',
    });
    const time = d.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Madrid',
    });

    return `Última actualización Meta: ${date} a las ${time}`;
  }

  close() {
    this.dialogRef.close(this.result);
  }

  submit() {
    if (!this.form.valid || this.syncing) {
      return;
    }

    this.syncing = true;
    this.error_message = null;
    this.result = null;

    this.api.syncMetaLeads(+this.form.value.hours).subscribe({
      next: result => {
        this.syncing = false;
        this.result = result;
        if (result.last_synced_at) {
          this.last_synced_at = result.last_synced_at;
        }
      },
      error: err => {
        this.syncing = false;
        this.error_message = err?.error?.error ?? err?.error?.message ?? 'No se pudo sincronizar con Meta.';
      },
    });
  }
}
